import { useCallback } from 'react';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { useSocketClient } from '../../../providers/SocketContext';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion';
import { AddSectionCommand } from '../UndoRedo/AddSectionCommand';

/**
 * Hook for adding sections with undo/redo support.
 *
 * Follows the command pattern:
 *   1. Construct AddSectionCommand
 *   2. command.execute() — sends SECTION_ADD to socket, updates Redux on success
 *   3. Push to undo stack on success
 *
 * Unlike block commands, section add is NOT optimistic because:
 *   - The sectionId is server-assigned (we don't know it until socket responds)
 *   - Adding an empty section to Redux before confirmation would show a blank
 *     section that might need to be removed on failure
 *
 * Returns { addedSectionId, cancelSection } matching the original useAddSection API.
 */
export const useAddSectionHandler = () => {
  const dispatch = useAppDispatch();
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    async ({ title, afterSectionId }: { title: string; afterSectionId: string }) => {
      const command = new AddSectionCommand({ dispatch, socketClient, checkDocumentLockStatus }, { title, afterSectionId });

      const sectionId = await command.execute();

      if (sectionId) {
        const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();
        undoRedoManager.pushUndoRedoCommands(command);

        return {
          addedSectionId: sectionId,
          cancelSection: () => {
            undoRedoManager.clearSectionCommands(sectionId);
          },
        };
      }

      throw new Error('Failed to add section');
    },
    [dispatch, socketClient, checkDocumentLockStatus]
  );
};
